import { useMemo, useState } from 'react';

import { ExpandButton } from '#/components/ExpandButton';
import { FieldGroup } from '#/components/Form/Field/FieldGroup';
import {
  FormContextConsumer,
  useFormContext,
} from '#/components/Form/FormContext';
import { InputField } from '#/components/Form/InputField';
import { MultiInputField } from '#/components/Form/MultiInputField';
import { PhoneField } from '#/components/Form/PhoneField';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';
import { colors } from '#/theme/theme';

type CollapseableFieldProps = {
  collapsed?: boolean;
};

function CollapseableLinkField(
  props: CollapseableFieldProps & { isEdit?: boolean },
) {
  const { isEdit, collapsed: initiallyCollapsed } = props;
  const [collapsed, setCollapsed] = useState(initiallyCollapsed ?? true);
  const { i18n } = useI18n();

  return (
    <div>
      <ExpandButton
        style={{ paddingLeft: 0 }}
        qaId="org-link-toggle"
        open={!collapsed}
        onClick={() => {
          setCollapsed((v) => !v);
        }}
      >
        {i18n('Add a Link')}
      </ExpandButton>

      {!collapsed && (
        <FormContextConsumer<{ orgType: ApiOrgType }>>
          {({ values }) => (
            <div
              style={{
                padding: '20px',
                backgroundColor: colors.backgroundBlue,
                borderRadius: '8px',
              }}
            >
              <MultiInputField
                name="links"
                label={
                  !isEdit && values.orgType === ApiOrgType.COMMUNITY
                    ? `${i18n('Links')}*`
                    : i18n('Links')
                }
                description={
                  values.orgType === ApiOrgType.CORPORATION
                    ? i18n(
                        'Links to your website and social media pages that reflect your social good initiatives.',
                      )
                    : i18n('Links to your website and social media pages.')
                }
                maxInputs={5}
                placeholder="https://example.org"
              />
            </div>
          )}
        </FormContextConsumer>
      )}
    </div>
  );
}

function CollapseableEmailField(props: CollapseableFieldProps) {
  const { collapsed: initiallyCollapsed } = props;
  const [collapsed, setCollapsed] = useState(initiallyCollapsed ?? true);
  const { i18n } = useI18n();
  return (
    <div>
      <ExpandButton
        style={{ paddingLeft: 0 }}
        qaId="org-email-toggle"
        open={!collapsed}
        onClick={() => {
          setCollapsed((v) => !v);
        }}
      >
        {i18n('Add an Email Address')}
      </ExpandButton>

      {!collapsed && (
        <div
          style={{
            padding: '20px',
            backgroundColor: colors.backgroundBlue,
            borderRadius: '8px',
          }}
        >
          <InputField
            name="email"
            type="email"
            label={i18n('Email')}
            description={i18n('Visible to the public')}
            placeholder={i18n('casey.smith@example.com')}
          />
        </div>
      )}
    </div>
  );
}

function CollapseablePhoneField(props: CollapseableFieldProps) {
  const { collapsed: initiallyCollapsed } = props;
  const [collapsed, setCollapsed] = useState(initiallyCollapsed ?? true);
  const { i18n } = useI18n();
  return (
    <div>
      <ExpandButton
        style={{ paddingLeft: 0 }}
        qaId="org-phone-toggle"
        open={!collapsed}
        onClick={() => {
          setCollapsed((v) => !v);
        }}
      >
        {i18n('Add a Phone Number')}
      </ExpandButton>

      {!collapsed && (
        <div
          style={{
            padding: '20px',
            backgroundColor: colors.backgroundBlue,
            borderRadius: '8px',
          }}
        >
          <PhoneField
            name="phone"
            label={i18n('Phone')}
            description={i18n('Visible to the public')}
            placeholder={i18n('e.g. 123 555-1234')}
          />
        </div>
      )}
    </div>
  );
}

type Props = {
  isEdit: boolean;
  isCommunity: boolean;
};

export function OrganizationContactField(props: Props) {
  const { isEdit, isCommunity } = props;

  const { i18n } = useI18n();
  const { errors } = useFormContext();

  const consolidatedErrors = useMemo(() => {
    const consolidated = [
      errors.orgContact,
      errors.links,
      errors.email,
      errors.phone,
    ].filter(Boolean);
    return consolidated.length > 0 ? consolidated : undefined;
  }, [errors]);

  return (
    <FieldGroup
      label={i18n('Contact Details *')}
      description={
        isCommunity
          ? i18n(
              'Enter at least one link. We will use this information to validate your organization.',
            )
          : i18n(
              'Pick at least one contact method. We use this information to validate your organization.',
            )
      }
      error={consolidatedErrors}
    >
      <CollapseableLinkField collapsed={!isEdit} isEdit={isEdit} />
      <CollapseableEmailField collapsed={!isEdit} />
      <CollapseablePhoneField collapsed={!isEdit} />
    </FieldGroup>
  );
}
